import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Mail, RotateCcw, RefreshCw } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MessageList, ReplyBox } from "@/components/Conversation";

export function TicketConversation({ ticket, onUpdated }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [open, setOpen] = useState(true);
  const { data: inbox } = useQuery({ queryKey: ["inbox-status"], queryFn: () => api.get("/inbox/status").then((r) => r.data), staleTime: 60000 });
  const reply = useMutation({
    mutationFn: async ({ text, subject, files }) => {
      let attachment_ids = [];
      if (files?.length) {
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f));
        const uploaded = await api.post(`/tickets/${ticket.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
        attachment_ids = uploaded.map((u) => u.file_id);
      }
      return api.post(`/tickets/${ticket.id}/reply`, { text, subject: subject || null, attachment_ids }).then((r) => r.data);
    },
    onSuccess: (t) => {
      onUpdated(t);
      qc.invalidateQueries({ queryKey: ["tickets"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      const last = [...(t.messages || [])].pop();
      toast[last?.email_status === "sent" ? "success" : "info"](last?.email_status === "sent" ? `Email sent to ${ticket.email}` : `Saved · email ${last?.email_status || "queued"}${last?.email_error ? ` — ${last.email_error}` : ""}`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const sync = useMutation({
    mutationFn: () => api.post("/inbox/sync").then((r) => r.data),
    onSuccess: async (d) => {
      if (d.status === "not_configured") return toast.info("Mailbox not connected yet — add IMAP_HOST / IMAP_USER / IMAP_PASS");
      if (d.status === "error") return toast.error(d.error);
      toast.success(`Inbox synced · ${d.processed} new email${d.processed === 1 ? "" : "s"}`);
      const t = await api.get(`/tickets/${ticket.id}`).then((r) => r.data);
      onUpdated(t);
      qc.invalidateQueries({ queryKey: ["tickets"] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const count = ticket.messages?.length || 0;
  const hasOutgoing = ticket.messages?.some((m) => m.from === "team");

  return (
    <div className="space-y-3 border-t-2 border-dashed border-zinc-300 pt-6" data-testid="ticket-conversation">
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full flex-wrap items-center gap-2 text-left font-display text-lg font-bold" data-testid="conversation-toggle">
        <Mail className="h-5 w-5" /> Conversation
        <span className="rounded-full bg-black px-2 py-0.5 text-xs text-white" data-testid="conversation-count">{count}</span>
        {ticket.repeat_issue && (
          <span className="pill border-[#F97316] bg-[#FFF7ED] text-[#C2410C]" title={ticket.reopened_from ? `Previously resolved by ${ticket.reopened_from}` : ""} data-testid="repeat-issue-pill">
            <RotateCcw className="h-3 w-3" /> Repeat issue · reopened {ticket.reopened_count}×{ticket.reopened_from ? ` after ${ticket.reopened_from}` : ""}
          </span>
        )}
        {ticket.needs_reply && <span className="pill border-[#DC2626] bg-[#FEF2F2] text-[#B91C1C]" data-testid="needs-reply-pill">Customer replied · awaiting your answer</span>}
        <span className="ml-auto text-xs font-semibold text-zinc-500">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <>
          <div className="flex flex-wrap items-center gap-2 text-sm text-zinc-600">
            <span>Full email thread with <strong>{ticket.email}</strong>. Send from here; replies land here{inbox?.connected ? " automatically every 2 minutes" : ""}.</span>
            {inbox && !inbox.connected && <span className="pill border-dashed border-zinc-500 bg-white text-zinc-600" data-testid="inbox-not-connected-pill">Mailbox not connected</span>}
            {user.role === "admin" && (
              <button type="button" className="btn-ghost ml-auto !py-1 !text-xs" onClick={() => sync.mutate()} disabled={sync.isPending} data-testid="inbox-sync-button">
                <RefreshCw className={`h-3.5 w-3.5 ${sync.isPending ? "animate-spin" : ""}`} /> Sync inbox
              </button>
            )}
          </div>
          <MessageList messages={ticket.messages} viewer="team" />
          <ReplyBox
            onSend={(text, clear, subject, files) => reply.mutate({ text, subject, files }, { onSuccess: clear })}
            busy={reply.isPending}
            withFiles
            placeholder={hasOutgoing ? "Write your reply… (Ctrl+Enter to send)" : "Write the first email to the customer… (Ctrl+Enter to send)"}
            testId="team-reply"
            label="Send email"
            withSubject
            defaultSubject={`[${ticket.ticket_id}] About your ${ticket.product} query`}
            templateVars={{ name: ticket.name.split(" ")[0], full_name: ticket.name, ticket_id: ticket.ticket_id, product: ticket.product, query: ticket.query, email: ticket.email, phone: ticket.phone, batch: ticket.batch || "", agent: user.name }}
          />
        </>
      )}
    </div>
  );
}
